// OP-1 — Controle físico-financeiro de entregas (visão consolidada Klaus).
// Lista entregáveis de TODOS os contratos filtrados por mês/ano/status etc.
// Cards no topo: R$ previsto · entregue · faturado · % atingimento.
// Click no entregável abre modal pra editar / marcar entregue / liberar BM.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Typography, Row, Col, Statistic, Table, Tag, Select, DatePicker,
  App as AntApp, Flex, Space, Button, Empty, Spin, message, Tooltip,
  Switch, InputNumber,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { CheckCircleOutlined, FileTextOutlined, DollarOutlined,
  DownOutlined, RightOutlined } from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number | null | undefined) => 'R$ ' + (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface ItemEntregavel {
  id: string;
  contrato_id: string;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  cliente_id: number | null;
  cliente_razao_social: string | null;
  numero_seq: number | null;
  descricao: string;
  valor: number | string | null;
  responsavel_email: string | null;
  status: string;
  data_prevista: string | null;
  data_entregue: string | null;
  data_medido: string | null;
  data_bm_enviado: string | null;
  data_aprovado_cliente: string | null;
  data_faturado: string | null;
  nf_numero: string | null;
  nf_emitida_em: string | null;
}

interface Metricas {
  valor_previsto: number;
  valor_entregue: number;
  valor_faturado: number;
  qtd_total: number;
  qtd_entregues: number;
  qtd_faturados: number;
  atingimento_pct: number | null;
}

const STATUS_OPCOES = [
  { value: 'pendente',  label: 'Pendente',  color: 'default' },
  { value: 'em_andamento', label: 'Em andamento', color: 'processing' },
  { value: 'entregue', label: 'Entregue', color: 'cyan' },
  { value: 'medido',   label: 'Medido',   color: 'geekblue' },
  { value: 'aprovado', label: 'Aprovado', color: 'purple' },
  { value: 'faturado', label: 'Faturado', color: 'success' },
];

const statusTag = (s: string) => {
  const o = STATUS_OPCOES.find(x => x.value === s);
  return <Tag color={o?.color || 'default'}>{o?.label || s}</Tag>;
};

// Edição inline de data prevista — click no texto abre DatePicker, salva no change.
// IMPORTANTE timezone: `valor` vem como ISO UTC (PG DATE → '2026-06-10T00:00:00Z').
// Parsear direto com dayjs converte pra timezone local (BRT -3h) e perde 1 dia.
// Solução: usar só os primeiros 10 chars (YYYY-MM-DD) — dayjs interpreta como
// data local 00:00, sem conversão. Mesmo padrão ao salvar.
function dataIso(v: string | null | undefined): string {
  return (v || '').slice(0, 10);
}
function EditDate({ valor, onSave, atrasado, aberto }: {
  valor: string | null; onSave: (v: string | null) => void;
  atrasado: boolean; aberto: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const ymd = dataIso(valor);
  if (editando) {
    return (
      <DatePicker autoFocus open size="small" format="DD/MM/YYYY"
        value={ymd ? dayjs(ymd) : null}
        onChange={(v) => { onSave(v ? v.format('YYYY-MM-DD') : null); setEditando(false); }}
        onOpenChange={(o) => { if (!o) setEditando(false); }}
        style={{ width: 130 }} />
    );
  }
  if (!ymd) {
    return (
      <Text type="warning" italic style={{ fontSize: 11, cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); setEditando(true); }}>
        {aberto ? '⚠ definir…' : '—'}
      </Text>
    );
  }
  return (
    <Text style={{
      fontSize: 12,
      color: atrasado ? '#B91626' : '#222',
      fontWeight: atrasado ? 600 : 400,
      cursor: 'pointer',
      borderBottom: '1px dotted #bbb',
    }} onClick={(e) => { e.stopPropagation(); setEditando(true); }}>
      {atrasado && '🔥 '}{dayjs(ymd).format('DD/MM/YYYY')}
    </Text>
  );
}

// Edição inline de valor — click abre InputNumber, salva no blur/enter.
function EditMoney({ valor, onSave }: {
  valor: number; onSave: (v: number) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [v, setV] = useState<number | null>(valor);
  useEffect(() => { setV(valor); }, [valor]);
  function commit() {
    setEditando(false);
    if (v != null && v !== valor) onSave(v);
  }
  if (editando) {
    return (
      <InputNumber autoFocus size="small" value={v}
        onChange={(n) => setV(typeof n === 'number' ? n : null)}
        onBlur={commit}
        onPressEnter={commit}
        prefix="R$" min={0} step={100}
        style={{ width: 110 }}
        formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
        parser={(s) => parseFloat((s || '').replace(/\./g, '')) as any} />
    );
  }
  return (
    <Text strong style={{ cursor: 'pointer', borderBottom: '1px dotted #bbb' }}
      onClick={(e) => { e.stopPropagation(); setEditando(true); }}>
      {'R$ ' + (valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
    </Text>
  );
}

function ControleEntregasInner() {
  const hoje = dayjs();
  const [mesRef, setMesRef] = useState<Dayjs>(hoje);
  const [statusFiltro, setStatusFiltro] = useState<string[]>([]);
  const [scopoAno, setScopoAno] = useState<boolean>(false);  // true = ano inteiro, false = só o mês
  // Filtros client-side
  const [clienteFiltro, setClienteFiltro] = useState<string[]>([]);
  const [contratoFiltro, setContratoFiltro] = useState<string[]>([]);
  const [soAtrasados, setSoAtrasados] = useState(false);
  const [esconderConcluidos, setEsconderConcluidos] = useState(false);
  // Collapse state — Set de contrato_ids colapsados
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());
  // Ordenação dos GRUPOS de contrato — sorter do header (Antd-controlado)
  const [sortBy, setSortBy] = useState<{ key: string; dir: 'ascend' | 'descend' } | null>(null);
  const [itens, setItens] = useState<ItemEntregavel[]>([]);
  const [metricas, setMetricas] = useState<Metricas | null>(null);
  const [loading, setLoading] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const body: any = { action: 'op_controle_entregas_listar', ano: mesRef.year() };
      if (!scopoAno) body.mes = mesRef.month() + 1;
      if (statusFiltro.length) body.status = statusFiltro;
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setItens(d.itens || []);
      setMetricas(d.metricas || null);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mesRef, statusFiltro, scopoAno]);

  async function marcarEntregue(item: ItemEntregavel) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_marcar_entregue', id: item.id },
        { headers: { Authorization: token } });
      message.success('Marcado como entregue');
      await carregar();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    }
  }

  async function liberarBm(item: ItemEntregavel) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_gerar_bm', entregavel_id: item.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) {
        navigator.clipboard.writeText(d.url).catch(() => {});
        message.success('BM gerado — link copiado pra área de transferência');
      } else {
        message.success('BM gerado');
      }
      await carregar();
    } catch (e: any) {
      message.error('Falha ao gerar BM: ' + (e.response?.data?.error || e.message));
    }
  }

  const labelPeriodo = useMemo(() => {
    const mes = mesRef.format('MMMM').replace(/^./, c => c.toUpperCase());
    return scopoAno ? `Ano ${mesRef.year()}` : `${mes} / ${mesRef.year()}`;
  }, [mesRef, scopoAno]);

  // Agrupa entregáveis por contrato_id, intercalando linhas-header
  // (com agregados do grupo) entre os blocos. Cada item conserva seu objeto
  // original; headers ficam marcados com __isGroupHeader.
  type Linha = (ItemEntregavel & { __isGroupHeader?: false }) | {
    __isGroupHeader: true;
    id: string;  // sintético, "header-<contrato_id>"
    contrato_id: string;
    contrato_numero: string | null;
    contrato_titulo: string | null;
    cliente_razao_social: string | null;
    grupo_qtd: number;
    grupo_abertos: number;
    grupo_atrasados: number;
    grupo_valor_previsto: number;
    grupo_valor_entregue: number;
  };
  // Aplica filtros client-side antes de agrupar
  const itensFiltrados = useMemo(() => {
    const hoje = dayjs().format('YYYY-MM-DD');
    return itens.filter(i => {
      if (clienteFiltro.length && !clienteFiltro.includes(i.cliente_razao_social || '')) return false;
      if (contratoFiltro.length && !contratoFiltro.includes(i.contrato_numero || '')) return false;
      if (esconderConcluidos && i.status === 'faturado') return false;
      if (soAtrasados) {
        const aberto = i.status === 'pendente' || i.status === 'em_andamento';
        const atrasado = aberto && dataIso(i.data_prevista) && dataIso(i.data_prevista) < hoje;
        if (!atrasado) return false;
      }
      return true;
    });
  }, [itens, clienteFiltro, contratoFiltro, esconderConcluidos, soAtrasados]);

  const linhas = useMemo<Linha[]>(() => {
    const hoje = dayjs().format('YYYY-MM-DD');
    const grupos = new Map<string, ItemEntregavel[]>();
    for (const it of itensFiltrados) {
      const k = it.contrato_id || '__sem_contrato__';
      if (!grupos.has(k)) grupos.set(k, []);
      grupos.get(k)!.push(it);
    }
    // Sumariza cada grupo pra usar no sort dos headers (Cliente/Contrato/Valor/Prevista).
    const arr = Array.from(grupos.entries()).map(([cid, items]) => {
      const minPrev = items.reduce<string>((m, x) => {
        const d = dataIso(x.data_prevista);
        if (!d) return m;
        return !m || d < m ? d : m;
      }, '');
      const valorTot = items.reduce((s, i) => s + (parseFloat(String(i.valor || 0)) || 0), 0);
      return {
        cid, items,
        minPrev: minPrev || '9999-99-99',
        valorTot,
        cliente: items[0]?.cliente_razao_social || '',
        contratoNum: items[0]?.contrato_numero || '',
      };
    });
    // Ordenação: respeita sortBy do header; senão default por data prevista mínima.
    const sortKey = sortBy?.key || 'data_prevista';
    const sortDir = sortBy?.dir || 'ascend';
    arr.sort((a, b) => {
      let c = 0;
      if (sortKey === 'cliente_razao_social') c = a.cliente.localeCompare(b.cliente);
      else if (sortKey === 'contrato_numero') c = a.contratoNum.localeCompare(b.contratoNum);
      else if (sortKey === 'valor') c = a.valorTot - b.valorTot;
      else c = a.minPrev.localeCompare(b.minPrev);  // default 'data_prevista'
      // Tiebreaker estável por número de contrato
      if (c === 0) c = a.contratoNum.localeCompare(b.contratoNum);
      return sortDir === 'descend' ? -c : c;
    });
    const result: Linha[] = [];
    for (const g of arr) {
      const first = g.items[0];
      const abertos = g.items.filter(i => i.status === 'pendente' || i.status === 'em_andamento').length;
      const atrasados = g.items.filter(i =>
        (i.status === 'pendente' || i.status === 'em_andamento') &&
        dataIso(i.data_prevista) && dataIso(i.data_prevista) < hoje
      ).length;
      const valorPrev = g.items.reduce((s, i) => s + (parseFloat(String(i.valor || 0)) || 0), 0);
      const valorEntr = g.items.filter(i =>
        ['entregue','medido','aprovado','faturado'].includes(i.status)
      ).reduce((s, i) => s + (parseFloat(String(i.valor || 0)) || 0), 0);
      result.push({
        __isGroupHeader: true,
        id: `header-${g.cid}`,
        contrato_id: g.cid,
        contrato_numero: first?.contrato_numero || null,
        contrato_titulo: first?.contrato_titulo || null,
        cliente_razao_social: first?.cliente_razao_social || null,
        grupo_qtd: g.items.length,
        grupo_abertos: abertos,
        grupo_atrasados: atrasados,
        grupo_valor_previsto: valorPrev,
        grupo_valor_entregue: valorEntr,
      });
      // Só adiciona os itens se o grupo NÃO está colapsado
      if (!colapsados.has(g.cid)) {
        result.push(...g.items);
      }
    }
    return result;
  }, [itensFiltrados, colapsados, sortBy]);

  // Opções derivadas pra Selects de filtro
  const opcoesCliente = useMemo(() => {
    const s = new Set<string>();
    for (const i of itens) if (i.cliente_razao_social) s.add(i.cliente_razao_social);
    return Array.from(s).sort().map(x => ({ value: x, label: x }));
  }, [itens]);
  const opcoesContrato = useMemo(() => {
    const s = new Set<string>();
    for (const i of itens) if (i.contrato_numero) s.add(i.contrato_numero);
    return Array.from(s).sort().map(x => ({ value: x, label: x }));
  }, [itens]);

  function toggleColapso(contratoId: string) {
    setColapsados(prev => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId); else next.add(contratoId);
      return next;
    });
  }
  function colapsarTodos() {
    const cids = new Set<string>();
    for (const i of itensFiltrados) cids.add(i.contrato_id || '__sem_contrato__');
    setColapsados(cids);
  }
  function expandirTodos() { setColapsados(new Set()); }

  // Salva campo do entregável (data prevista, valor, etc) — reusa op_atualizar_entregavel
  async function salvarCampo(item: ItemEntregavel, campo: string, valor: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_atualizar_entregavel', id: item.id, [campo]: valor },
        { headers: { Authorization: token } });
      message.success('Salvo');
      await carregar();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + (e.response?.data?.error || e.message));
    }
  }

  // Helper: detecta linha de cabeçalho de grupo
  const isHeader = (row: Linha): row is Extract<Linha, { __isGroupHeader: true }> =>
    (row as any).__isGroupHeader === true;

  const colunas = [
    {
      title: 'Cliente',
      key: 'cliente_razao_social',
      dataIndex: 'cliente_razao_social',
      width: 220,
      ellipsis: true,
      sorter: true,
      sortOrder: sortBy?.key === 'cliente_razao_social' ? sortBy.dir : null,
      render: (_: any, row: Linha) => {
        if (isHeader(row)) {
          const colapsado = colapsados.has(row.contrato_id);
          return (
            <Space size={4}>
              <Button type="text" size="small"
                icon={colapsado ? <RightOutlined /> : <DownOutlined />}
                onClick={(e) => { e.stopPropagation(); toggleColapso(row.contrato_id); }} />
              <Text strong style={{ color: '#020D7F', fontSize: 12 }}>{row.cliente_razao_social || '— sem cliente —'}</Text>
            </Space>
          );
        }
        return row.cliente_razao_social || <Text type="secondary" italic>—</Text>;
      },
    },
    {
      title: 'Contrato',
      key: 'contrato_numero',
      dataIndex: 'contrato_numero',
      width: 120,
      sorter: true,
      sortOrder: sortBy?.key === 'contrato_numero' ? sortBy.dir : null,
      render: (_: any, row: Linha) => {
        if (isHeader(row)) {
          return row.contrato_numero
            ? <Tooltip title={row.contrato_titulo}><Tag color="blue" style={{ fontWeight: 600 }}>{row.contrato_numero}</Tag></Tooltip>
            : <Text type="secondary">—</Text>;
        }
        return null;  // não repete numero do contrato nos itens (já está no header acima)
      },
    },
    {
      title: 'Entregável',
      dataIndex: 'descricao',
      ellipsis: true,
      render: (_: any, row: Linha) => {
        if (isHeader(row)) {
          return (
            <Text style={{ fontSize: 11, color: '#666' }}>
              <strong>{row.grupo_qtd}</strong> entregável(eis)
              {row.grupo_abertos > 0 && <> · <strong>{row.grupo_abertos}</strong> aberto(s)</>}
              {row.grupo_atrasados > 0 && <> · <span style={{ color: '#B91626' }}>🔥 <strong>{row.grupo_atrasados}</strong> atrasado(s)</span></>}
            </Text>
          );
        }
        return (
          <div>
            <Text strong style={{ fontSize: 12 }}>{row.numero_seq ? `${row.numero_seq}. ` : ''}{row.descricao}</Text>
            {row.responsavel_email && <div style={{ fontSize: 10, color: '#888' }}>resp: {row.responsavel_email.split('@')[0]}</div>}
          </div>
        );
      },
    },
    {
      title: 'Valor',
      key: 'valor',
      dataIndex: 'valor',
      width: 130,
      align: 'right' as const,
      sorter: true,
      sortOrder: sortBy?.key === 'valor' ? sortBy.dir : null,
      render: (_: any, row: Linha) => {
        if (isHeader(row)) {
          return (
            <div>
              <Text strong style={{ color: '#020D7F', fontSize: 12 }}>{fmt(row.grupo_valor_previsto)}</Text>
              {row.grupo_valor_entregue > 0 && (
                <div style={{ fontSize: 10, color: '#0a7f4a' }}>✓ {fmt(row.grupo_valor_entregue)}</div>
              )}
            </div>
          );
        }
        return <EditMoney valor={parseFloat(String(row.valor || 0)) || 0}
          onSave={(v) => salvarCampo(row, 'valor', v)} />;
      },
    },
    {
      title: 'Prevista',
      key: 'data_prevista',
      dataIndex: 'data_prevista',
      width: 140,
      sorter: true,
      sortOrder: sortBy?.key === 'data_prevista' ? sortBy.dir : null,
      render: (_: any, row: Linha) => {
        if (isHeader(row)) return null;
        const hoje = dayjs().format('YYYY-MM-DD');
        const ymd = dataIso(row.data_prevista);
        const aberto = row.status === 'pendente' || row.status === 'em_andamento';
        const atrasado = aberto && !!ymd && ymd < hoje;
        return (
          <EditDate valor={ymd || null} atrasado={atrasado} aberto={aberto}
            onSave={(v) => salvarCampo(row, 'data_prevista', v)} />
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (_: any, row: Linha) => isHeader(row) ? null : statusTag(row.status),
    },
    {
      title: 'NF',
      dataIndex: 'nf_numero',
      width: 90,
      render: (_: any, row: Linha) => {
        if (isHeader(row)) return null;
        return row.nf_numero ? <Tag color="success">{row.nf_numero}</Tag> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
      },
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 220,
      render: (_: any, row: Linha) => {
        if (isHeader(row)) return null;
        return (
          <Space size={4}>
            {row.status === 'pendente' || row.status === 'em_andamento' ? (
              <Button size="small" icon={<CheckCircleOutlined />} onClick={(e) => { e.stopPropagation(); marcarEntregue(row); }}>
                Entregar
              </Button>
            ) : null}
            {(row.status === 'entregue' || row.status === 'medido' || row.status === 'aprovado') && !row.data_bm_enviado ? (
              <Button size="small" type="primary" icon={<FileTextOutlined />} onClick={(e) => { e.stopPropagation(); liberarBm(row); }}>
                Liberar BM
              </Button>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Flex justify="space-between" align="start" wrap="wrap" gap={12} style={{ marginBottom: 18 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#020D7F' }}>📊 Controle de Entregas</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Trabalho em aberto + entregas concluídas em {labelPeriodo} · pendentes aparecem sempre, mesmo sem data prevista
          </Text>
        </div>
        <Space direction="vertical" size={4} align="end">
          <Space wrap>
            <DatePicker.MonthPicker value={mesRef} onChange={(v) => v && setMesRef(v)}
              format="MMM/YYYY" allowClear={false} size="small" style={{ width: 140 }} />
            <Select size="small" value={scopoAno ? 'ano' : 'mes'} onChange={(v) => setScopoAno(v === 'ano')}
              style={{ width: 110 }}
              options={[{ value: 'mes', label: 'Só do mês' }, { value: 'ano', label: 'Ano inteiro' }]} />
            <Select size="small" mode="multiple" placeholder="Status…" allowClear
              value={statusFiltro} onChange={setStatusFiltro}
              style={{ minWidth: 180, maxWidth: 280 }} maxTagCount="responsive"
              options={STATUS_OPCOES.map(o => ({ value: o.value, label: o.label }))} />
          </Space>
          <Space wrap>
            <Select size="small" mode="multiple" placeholder="Cliente…" allowClear
              value={clienteFiltro} onChange={setClienteFiltro}
              showSearch optionFilterProp="label"
              style={{ minWidth: 180, maxWidth: 280 }} maxTagCount="responsive"
              options={opcoesCliente} />
            <Select size="small" mode="multiple" placeholder="Contrato…" allowClear
              value={contratoFiltro} onChange={setContratoFiltro}
              showSearch optionFilterProp="label"
              style={{ minWidth: 130, maxWidth: 220 }} maxTagCount="responsive"
              options={opcoesContrato} />
            <Tooltip title="Mostra só entregáveis com data prevista no passado E ainda abertos">
              <span><Switch size="small" checked={soAtrasados} onChange={setSoAtrasados} /> <Text style={{ fontSize: 11 }}>Atrasados</Text></span>
            </Tooltip>
            <Tooltip title="Esconde entregáveis com status 'Faturado'">
              <span><Switch size="small" checked={esconderConcluidos} onChange={setEsconderConcluidos} /> <Text style={{ fontSize: 11 }}>Esconder faturados</Text></span>
            </Tooltip>
            <Button size="small" onClick={colapsados.size === 0 ? colapsarTodos : expandirTodos}>
              {colapsados.size === 0 ? '▶ Colapsar tudo' : '▼ Expandir tudo'}
            </Button>
          </Space>
        </Space>
      </Flex>

      {/* Métricas */}
      {metricas && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic title="Previsto" value={fmt(metricas.valor_previsto)} valueStyle={{ color: '#0930D0', fontSize: 22 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>{metricas.qtd_total} entregável(eis)</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ background: '#0a7f4a', color: '#fff', border: 'none' }}>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Entregue</Text>
              <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{fmt(metricas.valor_entregue)}</div>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>{metricas.qtd_entregues} entregue(s)</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic title="Faturado" value={fmt(metricas.valor_faturado)} prefix={<DollarOutlined />} valueStyle={{ color: '#02B5DB', fontSize: 22 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>{metricas.qtd_faturados} c/ NF</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic title="Atingimento" value={metricas.atingimento_pct ?? '—'}
                suffix={metricas.atingimento_pct != null ? '%' : ''}
                valueStyle={{ color: (metricas.atingimento_pct ?? 0) >= 100 ? '#0a7f4a' : (metricas.atingimento_pct ?? 0) >= 70 ? '#B07500' : '#B91626', fontSize: 22 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>R$ entregue ÷ previsto</Text>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabela */}
      {loading ? (
        <Flex justify="center" style={{ padding: 40 }}><Spin /></Flex>
      ) : linhas.length === 0 ? (
        <Empty description={`Sem entregáveis em ${labelPeriodo}`} />
      ) : (
        <Table size="small" rowKey="id" dataSource={linhas} columns={colunas as any}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['30','50','100','200'] }}
          onChange={(_p, _f, sorter) => {
            const s: any = Array.isArray(sorter) ? sorter[0] : sorter;
            if (s?.order && s?.columnKey) {
              setSortBy({ key: String(s.columnKey), dir: s.order });
            } else {
              setSortBy(null);
            }
          }}
          onRow={(row) => {
            if (isHeader(row)) {
              return {
                style: {
                  background: '#F0F5FF',
                  borderTop: '2px solid #d6e4ff',
                  cursor: 'default',
                  fontWeight: 600,
                },
              };
            }
            return { style: { cursor: 'default' } };
          }} />
      )}

    </div>
  );
}

export default function OperacionalControleEntregas() {
  return (
    <AntApp>
      <ControleEntregasInner />
    </AntApp>
  );
}
